import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxMessageToastService } from '@aposin/ng-aquila/message';
import { NxPopoverModule } from '@aposin/ng-aquila/popover';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { NxTableModule } from '@aposin/ng-aquila/table';
import { take } from 'rxjs';
import { Application } from '../../../models/application.model';
import { ApplicationService } from '../../../services/application.service';
import { MockDataService } from '../../../services/mock-data.service';
import { PermissionService } from '../../../services/permission.service';
import { isLocalEnvironment } from '../../../utils/environment.utils';

@Component({
  selector: 'app-application-table',
  standalone: true,
  imports: [
    NxTableModule,
    NxSpinnerModule,
    NxButtonModule,
    NxPopoverModule,
    AsyncPipe,
  ],
  templateUrl: './application-table.component.html',
  styleUrl: './application-table.component.scss'
})
export class ApplicationTableComponent implements OnChanges {
  private readonly applicationService = inject(ApplicationService);
  private readonly mockDataService = inject(MockDataService);
  private readonly messageToastService = inject(NxMessageToastService);
  permissionService = inject(PermissionService);

  @Input() pagerDutyServiceId: string | null = null;
  @Output() editApplication = new EventEmitter<Application>();
  @Output() createApplication = new EventEmitter<void>();
  @Output() deleteApplication = new EventEmitter<Application>();
  @Output() createFeature = new EventEmitter<Application>();
  @Output() editFeature = new EventEmitter<{ application: Application, featureId: string }>();
  @Output() deleteFeature = new EventEmitter<{ application: Application, featureId: string }>();

  applications: Application[] = [];
  selectedApplicationId: string | null = null;
  isApplicationsLoading = false;
  isPopoverOpen = false;

  ngOnChanges(): void {
    if (this.pagerDutyServiceId) {
      this.loadApplications(this.pagerDutyServiceId);
    } else {
      this.applications = [];
      this.selectedApplicationId = null;
    }
  }

  loadApplications(pagerDutyServiceId: string) {
    this.isApplicationsLoading = true;
    this.applications = [];

    const dataService = isLocalEnvironment()
      ? this.mockDataService.getApplicationsByServiceId(pagerDutyServiceId)
      : this.applicationService.getApplicationsByServiceId(pagerDutyServiceId);

    dataService
      .pipe(take(1))
      .subscribe((response) => {
        this.applications = response?.body || [];
        this.isApplicationsLoading = false;
      });
  }

  onApplicationSelect(application: Application) {
    if (this.selectedApplicationId === application.applicationId) {
      this.selectedApplicationId = null;
    } else {
      this.selectedApplicationId = application.applicationId;
    }
  }

  onEditApplication(application: Application) {
    this.editApplication.emit(application);
  }

  onCreateApplication() {
    this.createApplication.emit();
  }

  onDeleteApplication(application: Application) {
    this.deleteApplication.emit(application);
  }

  onCreateFeature(application: Application) {
    this.createFeature.emit(application);
  }

  onEditFeature(application: Application, featureId: string) {
    this.editFeature.emit({ application, featureId });
  }

  onDeleteFeature(application: Application, featureId: string) {
    this.deleteFeature.emit({ application, featureId });
  }

  onPopoverToggle(event: unknown): void {
    let isOpen: boolean;

    if (typeof event === 'boolean') {
      isOpen = event;
    } else if (event && typeof event === 'object') {
      const eventObj = event as Record<string, unknown>;
      isOpen = !!(eventObj['opened'] || eventObj['value'] || eventObj['isOpen']);
    } else {
      isOpen = false;
    }
    this.isPopoverOpen = isOpen;
  }

  onButtonClick(): void {
    this.isPopoverOpen = !this.isPopoverOpen;
  }

  updateApplicationInPlace(updatedApplication: Application) {
    const index = this.applications.findIndex(
      app => app.applicationId === updatedApplication.applicationId
    );

    if (index !== -1) {
      this.applications[index] = updatedApplication;
      this.applications = [...this.applications];
    } else {
      this.applications = [...this.applications, updatedApplication];
    }
  }

  refreshApplications() {
    if (this.pagerDutyServiceId) {
      this.loadApplications(this.pagerDutyServiceId);
    }
  }
}
